import { colors, colorSystem } from '@/styles/theme'
import { HStack, Icon, VStack } from '@musinsa/mwms-ds'
import { Switch } from '@/components/content/Switch/Switch'
import { tokens } from '@musinsa/layer-tokens/desktop'
import styled from 'styled-components'
import Text from '@/components/content/Text'
import { CapaItem, WarehouseHeaderCell } from './capacity/CapaItem'
import { useGetCapacityDaySummary } from '@/services/capacity/useCapacityQueries'
import { BaseCell } from './shared/BaseCell'
import { useGetTimeSlots } from '../hooks/useGetTimeSlots'
import { useCutOffDate } from '../hooks/useCutOffDate'
import { Suspense, useEffect, useRef } from 'react'
import { CutOffRow } from './cut-off/CutOffRow'
import { useGetCutOffWarehouses } from '@/services/warehouse/useWareHouseQueries'
import {
  LEFT_STICKY_WIDE_WIDTH,
  LEFT_STICKY_WIDTH,
  MENU_CODE,
  TIME_CELL_WIDTH,
  WAIT_TIME_CELL_WIDE_WIDTH,
  WAIT_TIME_CELL_WIDTH,
} from '../constants'
import { useGetOutboundSchedules, useUpsertGlobalCuttime } from '@/services/outboundSchedule2/useOutboundScheduleQueries'
import useModal from '@/hooks/useModal'
import { SetCutOperationTimeModal } from './capacity/SetCutOperationTimeModal'
import { useParsedQuery } from '@/hooks/useParsedQuery'
import { OutboundSchedulePageQueryParams } from '../types/query'
import MAlert from '@/utils/modal/MAlert'
import { Utils } from '@/utils/Utils'
import { getShipperScopeErrorMessage } from '@/utils/shipperScope'
import { AxiosError } from 'axios'
import dayjs from 'dayjs'

export function OutboundTable() {
  const { cutOffDate, shipperCode } = useCutOffDate()

  const { data: cutOffWarehouses } = useGetCutOffWarehouses()
  const sortedCutOffWarehouses = cutOffWarehouses?.sort((a, b) => a.warehouseName.localeCompare(b.warehouseName))

  const { data: capaSummaryData } = useGetCapacityDaySummary({ date: cutOffDate }, MENU_CODE)

  const timeSlots = useGetTimeSlots()
  // 본문 슬롯 중 하나라도 H+M 혼합 표기(60 초과 && 60의 배수 아님, 예: 70→'1H 10M')면 sticky·셀 너비 확장
  const hasWideWaitTime =
    timeSlots?.some((slot) => {
      const m = slot.waitTimeMinutes ?? 60
      return m > 60 && m % 60 !== 0
    }) ?? false
  const stickyWidth = hasWideWaitTime ? LEFT_STICKY_WIDE_WIDTH : LEFT_STICKY_WIDTH
  const cellWidth = hasWideWaitTime ? WAIT_TIME_CELL_WIDE_WIDTH : WAIT_TIME_CELL_WIDTH

  // 전체 센터 공통 무배당발 출고마감 시간 - v2 globalCuttime
  const { data: rawSchedulesData } = useGetOutboundSchedules({ date: cutOffDate, shipperCode, cutType: 'B2C' })
  const globalCuttime = rawSchedulesData?.globalCuttime
  const cutOffEndTime = globalCuttime?.cutTime?.slice(0, 5)

  const scrollContainerRef = useRef<HTMLDivElement>(null)

  // 초기 스크롤: "지난 시간대 보기" ON일 때 최근 지난 시간대 2개만 보이도록 위치 조정
  const { parsedQs } = useParsedQuery<OutboundSchedulePageQueryParams>()
  const showPastCutOff = parsedQs.showPastCutOff === 'true'

  const initialScrolledRef = useRef(false)
  useEffect(() => {
    if (!showPastCutOff) {
      initialScrolledRef.current = false
      return
    }
    if (initialScrolledRef.current) return
    if (!timeSlots || timeSlots.length === 0) return
    if (!scrollContainerRef.current) return

    const now = dayjs()
    const pastCount = timeSlots.filter((slot) => now.isAfter(dayjs(`${slot.date} ${slot.startTime}`))).length

    const VISIBLE_PAST_COUNT = 2
    const CAPA_DATA_HEIGHT = 72 // CapaDataRow 내부 HStack 높이 (36 × 2)
    const CUTOFF_ROW_HEIGHT = 42

    const hidePastCount = Math.max(0, pastCount - VISIBLE_PAST_COUNT)
    scrollContainerRef.current.scrollTop = CAPA_DATA_HEIGHT + hidePastCount * CUTOFF_ROW_HEIGHT
    initialScrolledRef.current = true
  }, [timeSlots, showPastCutOff])

  const warehouseRows = (sortedCutOffWarehouses ?? []).map(({ warehouseCode, warehouseName }) => {
    const capacities = capaSummaryData?.warehouses?.find((w) => w.warehouseCode === warehouseCode)?.capacities ?? []
    const b2cCapacity = capacities.find((c) => c.capacityType === 'B2C')
    return {
      warehouseCode,
      warehouseName,
      capacities,
      isOperation: !!b2cCapacity,
      isUnlimited: b2cCapacity?.isUnlimited,
    }
  })

  return (
    <TableWrap
      ref={scrollContainerRef}
      style={{ ['--left-sticky-width' as string]: `${stickyWidth}px`, ['--wait-time-cell-width' as string]: `${cellWidth}px` }}
    >
      {/* 센터명 헤더 - 상단 sticky */}
      <HeaderRow>
        <HeaderLeftWrap>
          <EmptyCell />
        </HeaderLeftWrap>
        {warehouseRows.map((row) => (
          <WarehouseHeaderCell
            key={row.warehouseCode}
            warehouseCode={row.warehouseCode}
            warehouseName={row.warehouseName}
            isOperation={row.isOperation}
            isUnlimited={row.isUnlimited}
          />
        ))}
      </HeaderRow>

      {/* 케파 데이터 - sticky 아님 */}
      <CapaDataRow>
        <CapaDataLeftWrap>
          <HStack style={{ height: 72 }}>
            <LabelCell style={{ borderBottom: 'none' }}>
              <Text kind="Body_12_regular">운영정책</Text>
            </LabelCell>
            <VStack style={{ flex: 1 }}>
              <CapaTypeCell>
                <Text kind="Body_12_regular">케파(B2C)</Text>
              </CapaTypeCell>
              <CapaTypeCell>
                <Text kind="Body_12_regular">컷 마감시간</Text>
              </CapaTypeCell>
            </VStack>
          </HStack>
        </CapaDataLeftWrap>
        <CapaDataRight>
          <CapaB2CRowInner>
            {warehouseRows.map((row) => (
              <CapaItem
                key={row.warehouseCode}
                warehouseCode={row.warehouseCode}
                warehouseName={row.warehouseName}
                capacities={row.capacities}
              />
            ))}
          </CapaB2CRowInner>
          <CutOffEndTimeRow
            endTime={cutOffEndTime}
            isEnabled={globalCuttime?.isEnabled ?? false}
            cutTime={globalCuttime?.cutTime}
            scrollContainerRef={scrollContainerRef}
            stickyWidth={stickyWidth}
          />
        </CapaDataRight>
      </CapaDataRow>

      {/* 시간대 슬롯 */}
      {timeSlots
        ?.filter((timeRow) => timeRow.warehouses.length > 0)
        .map((timeRow) => (
          <CutOffRow data={timeRow} scrollContainerRef={scrollContainerRef} key={timeRow.startTime} warehouses={sortedCutOffWarehouses ?? []} />
        ))}
      {/* 시간대 슬롯 end */}
    </TableWrap>
  )
}

const TableWrap = styled.div`
  width: 100%;
  margin-top: 16px;
  border-top: 1.5px solid ${colors.black};
  overflow: auto;
  /* horizontal scrollbar가 마지막 row를 덮지 않도록 하단 여백 확보 */
  scrollbar-gutter: stable;
  padding-bottom: 14px;
`

const HeaderRow = styled.div`
  min-width: fit-content;
  background-color: ${colors.white};
  z-index: 99;

  display: flex;
  position: sticky;
  top: 0;
`

const HeaderLeftWrap = styled.div`
  min-width: var(--left-sticky-width);
  background-color: ${colors.white};
  z-index: 9;

  position: sticky;
  left: 0;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 1px;
    background-color: ${colors.gray400};
    pointer-events: none;
  }
`

const CapaDataRow = styled.div`
  min-width: fit-content;
  border-bottom: 1px solid ${colorSystem.text.common.secondary};
  background-color: ${colors.white};

  display: flex;
`

const CapaDataRight = styled.div`
  display: flex;
  flex-direction: column;
`

const CapaB2CRowInner = styled.div`
  display: flex;
  border-bottom: 1px solid ${colorSystem.border.table};
`

function CutOffEndTimeRow({
  endTime,
  isEnabled,
  cutTime,
  scrollContainerRef,
  stickyWidth,
}: {
  endTime?: string
  isEnabled: boolean
  cutTime?: string
  scrollContainerRef: React.RefObject<HTMLDivElement>
  stickyWidth: number
}) {
  const modal = useModal()
  const stickyRef = useRef<HTMLDivElement>(null)
  const { cutOffDate, shipperCode } = useCutOffDate()
  const upsertGlobalCuttime = useUpsertGlobalCuttime()

  useEffect(() => {
    const container = scrollContainerRef.current
    const sticky = stickyRef.current
    if (!container || !sticky) return

    const update = () => {
      sticky.style.setProperty('--visible-width', `${container.clientWidth - stickyWidth}px`)
    }

    update()

    const observer = new ResizeObserver(update)
    observer.observe(container)

    return () => observer.disconnect()
  }, [scrollContainerRef, stickyWidth])

  // 상단 Switch 토글: 당일만(applyDate=cutOffDate) 사용여부 변경. 현재 cutTime 함께 upsert
  const handleToggle = (next: boolean) => {
    if (!cutTime) return
    upsertGlobalCuttime.mutate(
      { shipperCode, cutType: 'B2C', cutTime, isEnabled: next, applyDate: cutOffDate },
      {
        onError: (error) => {
          MAlert.show(getShipperScopeErrorMessage(error) ?? Utils.getApiErrMessage(error as AxiosError<ApiResponseV2>, '출고마감 사용여부 변경에 실패했습니다.'))
        },
      },
    )
  }

  return (
    <>
      <CutOffEndTimeWrap>
        <StickyInner ref={stickyRef}>
          <ClickableInner onClick={modal.onOpenModal}>
            <EndTimeText $isDisabled={!isEnabled}>무배당발 출고마감 {isEnabled ? (endTime ?? '-') : '적용안함'}</EndTimeText>
            <Icon className="edit-icon" name="edit" size={16} color="gray800" />
          </ClickableInner>
          <AccentSwitchWrap onClick={(e) => e.stopPropagation()}>
            <Switch size="sm" checked={isEnabled} onCheckedChange={handleToggle} />
          </AccentSwitchWrap>
        </StickyInner>
      </CutOffEndTimeWrap>

      {modal.open && (
        <Suspense fallback={null}>
          <SetCutOperationTimeModal endTime={endTime} isEnabled={isEnabled} onClose={modal.onCloseModal} />
        </Suspense>
      )}
    </>
  )
}

const CutOffEndTimeWrap = styled.div`
  height: 36px;
  position: relative;
`

const StickyInner = styled.div`
  position: sticky;
  left: var(--left-sticky-width);
  width: var(--visible-width, 100%);
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
`

const ClickableInner = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;

  .edit-icon {
    display: none;
  }

  &:hover {
    .edit-icon {
      display: block;
    }
  }
`

const AccentSwitchWrap = styled.div`
  display: flex;
  align-items: center;

  input:checked + label {
    background-color: ${tokens.layerDesktop['bg-accent']};
  }
`

const EndTimeText = styled.span<{ $isDisabled: boolean }>`
  font-family: -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  font-feature-settings: 'zero' 0;
  color: ${({ $isDisabled }) => ($isDisabled ? colorSystem.text.common.disabled : 'inherit')};
`

const CapaDataLeftWrap = styled(VStack)`
  min-width: var(--left-sticky-width);
  background-color: ${colors.white};
  z-index: 9;

  position: sticky;
  left: 0;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 1px;
    background-color: ${colors.gray400};
    pointer-events: none;
  }
`

const EmptyCell = styled(BaseCell)`
  height: 44px;
  border-bottom: 1px solid ${colors.gray400};
`

const LabelCell = styled(BaseCell)`
  width: ${TIME_CELL_WIDTH}px;

  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 4px;
`

const CapaTypeCell = styled(BaseCell)`
  flex: 1;
  align-items: center;

  &:last-child {
    border-bottom: none;
  }
`
